import { useCallback, useEffect, useRef, useState } from 'react';

import { getResultSummary } from '../../../api/emissionsApi';
import { normalizeResultSummary } from '../model/resultSummary';

const EMPTY_STATE = Object.freeze({ data: null, loading: false, error: null });

/**
 * Loads the calculation result for one imported source. The request fires
 * only while `enabled` is true, so a closed dialog never calls the API.
 * A sequence guard plus AbortController drop stale responses when the
 * dialog reopens or the source/year changes mid-flight.
 */
export default function useResultSummary({ sourceId, year, enabled }) {
  const [state, setState] = useState(EMPTY_STATE);
  const sequenceRef = useRef(0);
  const controllerRef = useRef(null);

  const load = useCallback(async () => {
    if (!sourceId || !year) return;
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState(current => ({ ...current, loading: true, error: null }));
    try {
      const payload = await getResultSummary(
        { fiscal_year: Number(year), source: sourceId },
        { signal: controller.signal },
      );
      if (controller.signal.aborted || sequenceRef.current !== sequence) return;
      setState({ data: normalizeResultSummary(payload), loading: false, error: null });
    } catch (loadError) {
      if (controller.signal.aborted || sequenceRef.current !== sequence) return;
      setState(current => ({ ...current, loading: false, error: loadError }));
    }
  }, [sourceId, year]);

  useEffect(() => {
    if (!enabled) {
      sequenceRef.current += 1;
      controllerRef.current?.abort();
      setState(EMPTY_STATE);
      return undefined;
    }
    load();
    return () => controllerRef.current?.abort();
  }, [enabled, load]);

  return { ...state, reload: load };
}